import { Component, contentChildren, DestroyRef, inject, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ClColumn, ClPaginator, ClRow, ClSort, ClTable } from '@corelabs/angular/ui';
import { TranslatePipe } from '@ngx-translate/core';
import { tap } from 'rxjs';
import { patchParams } from '@shared/utils/patch-params';

export interface TableLoadParams {
    page: number;
    limit: number;
    sort?: string;
    order?: 'asc' | 'desc';
}

const DEFAULT_LIMIT = 20;

/** Paged, sortable list around the projected `<tr clRow>`s; URL matrix params are the single source of truth — see CLAUDE.md › Paginated Lists. */
@Component({
    selector: 'app-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.css'],
    imports: [ClTable, ClPaginator, TranslatePipe],
})
export class TableComponent implements OnInit {
    readonly total = input.required<number>();
    /** Header definitions with pre-translated labels; `key` is the sort field for sortable columns. */
    readonly columns = input.required<readonly ClColumn[]>();
    /** Rows-per-page choices; must contain DEFAULT_LIMIT, the value the URL clamp falls back to. */
    readonly rowsPerPageOptions = input<number[]>([10, DEFAULT_LIMIT, 50]);

    /** Fires whenever the URL params change; the parent fetches its data with these. */
    readonly loadData = output<TableLoadParams>();

    /** cl-table's own query can't see rows re-projected through ng-content, so its empty state is fed from here. */
    protected readonly rows = contentChildren(ClRow, { descendants: true });

    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly destroyRef = inject(DestroyRef);

    // Paginator and header state, mirrored from the URL.
    protected readonly page = signal(1);
    protected readonly limit = signal(DEFAULT_LIMIT);
    protected readonly sort = signal<ClSort | null>(null);

    ngOnInit(): void {
        // Not the constructor: paramMap emits on subscribe, and (loadData) is only wired by ngOnInit.
        this.route.paramMap
            .pipe(
                tap((params) => {
                    const rawPage = params.get('page');
                    const rawLimit = params.get('limit');

                    // Sanitize against tampering — these values reach the backend verbatim.
                    const limit = this.rowsPerPageOptions().includes(Number(rawLimit)) ? Number(rawLimit) : DEFAULT_LIMIT;
                    const page = Math.max(1, Math.trunc(Number(rawPage)) || 1);
                    const sort = params.get('sort') ?? undefined;
                    const order = params.get('order') === 'desc' ? 'desc' : 'asc';

                    // Rewriting re-enters this subscription with clean params, and that pass drives the load.
                    if (rawPage !== String(page) || rawLimit !== String(limit)) {
                        this.patchParams({ page, limit }, { replace: true });
                        return;
                    }

                    this.page.set(page);
                    this.limit.set(limit);
                    this.sort.set(sort ? { key: sort, order } : null);

                    this.loadData.emit(sort ? { page, limit, sort, order } : { page, limit });
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    /** `limit` comes from the paginator, not the URL: a size change emits pageSizeChange then pageChange(1). */
    protected onPage(page: number, limit: number): void {
        this.patchParams({ page, limit });
    }

    protected onLimit(limit: number): void {
        this.patchParams({ page: 1, limit });
    }

    protected onSort(sort: ClSort | null): void {
        this.patchParams({ sort: sort?.key ?? null, order: sort?.order ?? null }, { resetPage: true });
    }

    /** Merges matrix params on the current route, preserving ones this table doesn't own (e.g. a parent's `search`). */
    patchParams(partial: Record<string, string | number | null>, options?: { resetPage?: boolean; replace?: boolean }): void {
        patchParams(this.router, this.route, options?.resetPage ? { page: 1, ...partial } : partial, options?.replace);
    }
}
